import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { ArrowRight, Megaphone } from "lucide-react";
import { cn } from "@/lib/utils";
import { sanitizeRichText } from "@/lib/sanitize";

interface Announcement {
  id: string;
  title: string;
  content: string | null;
  link_url: string | null;
  link_text: string | null;
  badge_text: string | null;
  bg_color: string | null;
}

const Announcements = () => {
  const { data: announcements, isLoading } = useQuery({
    queryKey: ["announcements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("announcements")
        .select("id, title, content, link_url, link_text, badge_text, bg_color")
        .order("display_order", { ascending: true })
        .limit(3);

      if (error) throw error;
      return data as Announcement[];
    },
  });

  if (isLoading || !announcements || announcements.length === 0) {
    return null;
  }

  const getBgClass = (color: string | null) => {
    switch (color) {
      case "primary":
        return "bg-primary text-primary-foreground";
      case "secondary":
        return "bg-secondary text-secondary-foreground";
      case "accent":
        return "bg-accent text-accent-foreground";
      case "destructive":
        return "bg-destructive text-destructive-foreground";
      case "warning":
        return "bg-amber-500 text-white";
      default:
        return "bg-primary text-primary-foreground";
    }
  };

  return (
    <section className="py-4 md:py-6">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-3">
          {announcements.map((announcement) => (
            <div
              key={announcement.id}
              className={cn(
                "rounded-xl md:rounded-2xl p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-nature",
                getBgClass(announcement.bg_color)
              )}
            >
              <div className="flex items-start sm:items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <Megaphone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {announcement.badge_text && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20">
                        {announcement.badge_text}
                      </span>
                    )}
                    <h3 className="font-bold text-sm md:text-base">{announcement.title}</h3>
                  </div>
                  {announcement.content && (
                    <div 
                      className="text-sm opacity-90 line-clamp-2 prose-content-inline"
                      dangerouslySetInnerHTML={{ __html: sanitizeRichText(announcement.content) }}
                    />
                  )}
                </div>
              </div>
              {announcement.link_url && (
                <Link
                  to={announcement.link_url}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  {announcement.link_text || "Ver más"}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Announcements;
